import { PixKeyType } from '../../../helpers'
import {
  CouponCommissionType,
  CouponCommissionTypeFilter,
  FreeCouponCategory,
  IEventCoupon
} from '../../../services/EventCoupons/interfaces'

type DiscountType = IEventCoupon['discount_type']

/** Single source for the coupon vocabulary, shared by the listing and the form. */
export const discountTypeOptions: { value: DiscountType; label: string }[] = [
  { value: 'percentage', label: 'Percentual (%)' },
  { value: 'fixed', label: 'Valor fixo (R$)' },
  { value: 'free_ticket', label: 'Ingresso grátis' },
  { value: 'fee_free', label: 'Taxa grátis' }
]

export const commissionTypeOptions: { value: CouponCommissionType; label: string }[] = [
  { value: 'teacher', label: 'Professor' },
  { value: 'partner', label: 'Parceiro' },
  { value: 'commercial', label: 'Comercial' }
]

/** Opções da barra de filtros: os três tipos mais o balde que a coluna mostra como "—". */
export const commissionTypeFilterOptions: { value: CouponCommissionTypeFilter; label: string }[] = [
  ...commissionTypeOptions,
  { value: 'none', label: 'Sem comissionamento' }
]

export const freeCouponCategoryOptions: { value: FreeCouponCategory; label: string }[] = [
  { value: 'partner', label: 'Parceiro' },
  { value: 'staff', label: 'Staff' },
  { value: 'contest_guest', label: 'Convidado de participante de concurso' },
  { value: 'pedagogical', label: 'Pedagógico' }
]

export const pixKeyTypeOptions: { value: PixKeyType; label: string }[] = [
  { value: 'cpf_cnpj', label: 'CPF/CNPJ' },
  { value: 'email', label: 'E-mail' },
  { value: 'phone', label: 'Telefone' },
  { value: 'random', label: 'Chave aleatória' }
]

const toLabelMap = <T extends string>(options: { value: T; label: string }[]) =>
  options.reduce((acc, option) => ({ ...acc, [option.value]: option.label }), {} as Record<T, string>)

export const discountTypeLabels = toLabelMap(discountTypeOptions)
export const commissionTypeLabels = toLabelMap(commissionTypeOptions)
export const freeCouponCategoryLabels = toLabelMap(freeCouponCategoryOptions)
export const pixKeyTypeLabels = toLabelMap(pixKeyTypeOptions)

/**
 * Versão curta dos rótulos, mais o balde `none`, para o card de cortesias da tela do evento.
 *
 * São dois mapas de propósito: "Convidado de participante de concurso" é o rótulo correto no
 * formulário, mas não cabe na legenda do card. E `none` só existe do lado do relatório —
 * cobre tanto a cortesia sem cupom nenhum quanto o cupom gratuito antigo, criado antes de a
 * categoria existir.
 */
export const freeCouponCategoryShortLabels: Record<FreeCouponCategory | 'none', string> = {
  partner: 'Parceiro',
  staff: 'Staff',
  contest_guest: 'Convidado de concurso',
  pedagogical: 'Pedagógico',
  none: 'Não classificado'
}

export const freeCouponCategoryColors: Record<FreeCouponCategory | 'none', string> = {
  partner: '#DD6B20',
  staff: '#805AD5',
  contest_guest: '#3182CE',
  pedagogical: '#319795',
  none: '#CBD5E0'
}

/** Plural form used where a chart or a filter groups sales by who they commission. */
export const commissionTypePluralLabels: Record<CouponCommissionType, string> = {
  teacher: 'Professores',
  partner: 'Parceiros',
  commercial: 'Comercial'
}

/** Name of whoever a coupon commissions, whatever kind of beneficiary it is. */
export function commissionBeneficiaryName(coupon: IEventCoupon): string | null {
  if (coupon.commission_type === 'teacher') return coupon.teacher?.name ?? null
  if (coupon.commission_type === 'commercial') return coupon.commercial_consultant?.name ?? null
  if (coupon.commission_type === 'partner') return coupon.partner_name
  return null
}
